import React, { useEffect, useRef, useState } from 'react';

interface RevealProps {
  children: React.ReactNode;
  width?: "fit-content" | "100%";
  delay?: number;
  direction?: "up" | "down" | "left" | "right";
  className?: string;
}

export const Reveal: React.FC<RevealProps> = ({ 
  children, 
  width = "fit-content", 
  delay = 0, 
  direction = "up",
  className = "" 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Wait for animation duration (1000ms) + delay to finish, then allow overflow
          // This prevents clipping of elements like glows or shadows that extend beyond the box
          const timer = setTimeout(() => {
            setIsAnimationComplete(true);
          }, 1000 + (delay * 1000));
          
          observer.disconnect();
          return () => clearTimeout(timer);
        }
      },
      { threshold: 0.2 } // Trigger when 20% visible
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  const getTransform = () => {
    if (!isVisible) {
      switch (direction) {
        case "up": return "translate-y-20";
        case "down": return "-translate-y-20";
        case "left": return "-translate-x-20";
        case "right": return "translate-x-20";
        default: return "translate-y-20";
      }
    }
    return "translate-x-0 translate-y-0";
  };

  return (
    <div 
      ref={ref} 
      style={{ width }} 
      // Switch to overflow-visible after animation to prevent clipping
      className={`relative ${isAnimationComplete ? 'overflow-visible' : 'overflow-hidden'} ${className}`}
    >
      <div
        className={`transition-all duration-1000 ease-out transform ${
          isVisible ? "opacity-100" : "opacity-0"
        } ${getTransform()}`}
        style={{ transitionDelay: `${delay}s` }}
      >
        {children}
      </div>
    </div>
  );
};